import isNumber from 'lodash/isNumber';
import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import PropTypes from 'prop-types';
import { stringify } from 'query-string';
import classNames from 'classnames';
import { CONTENT_ARCHIVE } from 'config/types/prop';
import { options } from 'config/reactRedux';
import createGetTranslation from 'selectors/i18n';
import {
  getSearchPath,
  getSearchParams,
  getSearchResultsShowing,
} from 'selectors/searchSelectors';
import getActivePagination from 'selectors/getActivePagination';
import withStyles from 'isomorphic-style-loader/withStyles';
import ContentListWithLoader from 'components/contentList/contentListWithLoader';
import Pagination from 'components/pagination';
import FilterMenu from 'components/filterMenu';
import ButtonControl from 'components/filterMenu/buttonControl';
import ThemeProvider from 'components/hoc/themeProvider';
import Meta from 'components/meta';
import I18n from 'components/i18n';

import SearchField from './searchField';
import styles from './search.css';

const SearchArchive = ({
  pagination: {
    hasMore,
    currentPage,
    totalPages,
    paginationLinks,
  },
  pageMeta: {
    controls: {
      order,
    },
  },
  onChangeFilter,
  resultsShowing,
  items,
  total,
  search,
  params,
  getTranslation,
}) => {
  const hasItems = !!items.length;
  const hasResults = !!total && isNumber(total) && params.s.trim();
  const showPagination = hasMore ||
    (currentPage === totalPages && 1 < totalPages);

  const searchPageName = getTranslation('search', 'Search Articles');
  const magazineName = getTranslation('meta-title', 'Babbel Magazine');
  const titleText = `${searchPageName} - ${magazineName}`;

  return (
    <div className={styles.wrapper}>
      <Meta title={titleText} />
      <h1 className="hidden">{titleText}</h1>
      <div className={styles.breakWordTable}>
        <h2 className={styles.heading}>
          {params.s ?
            <span>
              <I18n
                field="search-results-for"
                defaultText="Search Results for"
              />{` "${params.s}"`}
            </span>
            :
            <I18n
              field="search"
              defaultText="Search"
            />
          }
        </h2>
      </div>
      <SearchField onSubmit={search} params={params} />
      <div className={styles.contentWrapper}>
        {hasResults && <div className={styles.content}>
          <div>
            {!!total && !!resultsShowing.length &&
              <span className={styles.resultCount}>
                <I18n
                  field="showing_search1"
                  defaultText="Showing"
                />
                {` ${resultsShowing[0]}-${resultsShowing[1]} `}
                <I18n
                  field="of_search2"
                  defaultText="of"
                />
                {` ${total} `}
                <I18n
                  field="results_search3"
                  defaultText="Results"
                />
              </span>
            }
          </div>
          <div className={styles.filters}>
            <FilterMenu>
              <ButtonControl
                label={<I18n
                  field="sort-by"
                  defaultText="Sort By"
                />}
                name="order"
                options={order.options}
                selected={order.selected || 'relevance'}
                onChange={onChangeFilter}
              />
            </FilterMenu>
          </div>
          {hasItems &&
            <ThemeProvider themes={{
              DefaultListViewItem: 'linear',
              ContentList: 'linear',
            }}
            >
              <ContentListWithLoader
                items={items}
                loading={!hasItems}
              />
            </ThemeProvider>
          }
          {showPagination && <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            links={paginationLinks}
          />}
        </div>}
        {!hasResults && <div className={styles.empty}>
          <h2 className={styles.emptyHeading}>
            <I18n
              field="no-search-results1"
              defaultText="Uh Oh!"
            />
          </h2>
          <p>
            <I18n
              field="no-search-results2"
              defaultText="We can&apos;t seem to find any results for:"
            />
            <div
              className={classNames(
                styles.searchTermWrapper,
                styles.breakWordTable,
              )}
            >
              <span className={styles.searchTerm}> {params.s}</span>
            </div>
          </p>
          <p>
            <I18n
              field="no-search-results3"
              defaultText="Try searching for something else."
            />
          </p>
        </div>}
      </div>
    </div>
  );
};

SearchArchive.propTypes = {
  ...CONTENT_ARCHIVE,
  search: PropTypes.func.isRequired,
  params: PropTypes.object.isRequired,
  total: PropTypes.number.isRequired,
  resultsShowing: PropTypes.array.isRequired,
  onChangeFilter: PropTypes.func.isRequired,
};

SearchArchive.defaultProps = {
  hasMore: false,
};

const mapStateToProps = (state) => {
  const { total } = getActivePagination(state);
  return {
    searchPath: getSearchPath(state),
    params: getSearchParams(state),
    resultsShowing: getSearchResultsShowing(state),
    total: total || 0,
    getTranslation: createGetTranslation(state),
  };
};

export const mapDispatchToProps = (dispatch) => ({
  search: (searchPath) => (params) => {
    const queryString = stringify(params, { arrayFormat: 'bracket' });
    dispatch(push(`${searchPath}?${queryString}`));
  },
});

export const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...ownProps,
  search: dispatchProps.search(stateProps.searchPath),
});

const withRedux = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  options,
);

export default withRedux(withStyles(styles)(SearchArchive));
